import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListPagination from "@MasterBluePrint/Components/ProductListPagination";
import ProductCountDisplay from "@MasterBluePrint/Components/ProductListProductCount";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListControlsStyles {
    wrapper?: InjectableCss;
    select?: SelectPresentationProps;
    textField?: TextFieldPresentationProps;
    labelText: InjectableCss;   
    leftColumn: InjectableCss;
    resultsCount: InjectableCss;
    resultsPerPage?: InjectableCss;
    centerColumn: InjectableCss;
    paginationNav?: InjectableCss;
    rightColumn: InjectableCss;
    sortBySelect: InjectableCss;
    jumpToPage?: InjectableCss;
}

export const productListControlStyles: ProductListControlsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-flow:row;
            padding:0 27px;
            @media (max-width: 991px) {
                flex-flow: row wrap;
            }
            @media (max-width: 768px) {
                padding:0 12px;
            }
            @media (max-width: 575px) {
                padding:0 21px;
            }
        `,
    },
    leftColumn: {
        css: css`
            flex: 1 33%;
            display: flex;
            flex-flow:row wrap;
            justify-content: flex-start;
            order:1;
            div[class^="PaginationStyle"], span[class*=" PaginationStyle"] {
                flex-direction:row;
                justify-content:flex-start;
                align-items:flex-end;
            }
            nav[aria-label="Pagination navigation"] {display:none;}
            @media (max-width: 991px) {
                flex:1 50%;
            }
            @media (max-width: 768px) {
                max-width:50%;
            }
        `
    },
    resultsCount: {
        css: css`
            flex:1 100%;
            display:flex;
            align-items:center;
            height: 40px;
            margin:6px 0;
            > div {margin-top:0;}
            @media (max-width: 575px) {
                margin-top:16px;
                > div {max-width:133px;}
            }
        `,
    },
    resultsPerPage: {
        css: css`
            label {
                padding: 9px 0 0 0;
                width:auto;
                font-weight:500 !important;
            }
            flex:1 100%;
            display:flex;
            align-items:flex-end;
            margin:6px 0 0;
            > div > span {margin:0;}
            @media (max-width: 575px) {
                > div > span > div {
                    display:flex;
                    flex-flow:row wrap;
                    >label, > div {
                        flex:1 100%;
                    }
                }
            }
        `,
    },
    centerColumn: {
        css: css`
            flex: 1 34%;
            display: flex;
            order:2;
            span[class^="PerPageSelect"], span[class*=" PerPageSelect"] {display:none;}
            div[class^="PaginationStyle"], span[class*=" PaginationStyle"] {
                flex-direction:row;
                justify-content:center;
                align-items:flex-end;
            }
            nav[aria-label="Pagination navigation"] {
                margin:0;
                transform:none;                
                @media (min-width: 1200px) {transform: translateX(-18%);}
                @media (min-width: 991px) and (max-width: 1199px) {transform: translateX(-9%);}
            }
            @media (max-width: 991px) {
                order:3;
                flex:1 100%;
                margin-top:12px;
            }
        `
    },
    paginationNav: {
        css: css`
            flex:1 100%;
            display:flex;
            margin-bottom:6px;
            @media (max-width: 991px) {margin-bottom:-6px;}
        `,
    },
    rightColumn: {
        css: css`
            flex: 1 33%;
            display: flex;
            flex-flow:row wrap;
            order:3;
            @media (max-width: 1200px) {
                > div > div > div {
                    width:133px;
                    min-width:0; 
                }
            }
            @media (max-width: 991px) {
                order:2;
                flex:1 50%;
            }
            @media (max-width: 768px) {
                max-width:50%;
            }
        `
    },
    sortBySelect: {
        css: css`
            flex:1 100%;
            margin:6px 0;
            text-align:right;
            label {
                text-align:left;
                padding: 9px 0 0 0;
                font-weight:500;
            }
            @media (max-width: 575px) {
                > div {
                    display:flex;
                    flex-flow:row wrap;
                    justify-content:flex-end;
                    > label, > div {
                        flex:1 100%;
                        max-width:133px;
                    }
                    > label > span {display:none;}
                }
            }
        `,
    },
    jumpToPage: {
        css: css`
            flex:1 100%;
            margin:6px 0 0;
            text-align:right;
            span {
                padding:0 15px 0;
                font-weight:500;
            }
            @media (max-width: 1200px) {
                input:not(.react-datetime-picker__inputGroup__input) {
                    min-width:0;
                    padding-right:10px !important;
                }
            }
            @media (max-width: 575px) {
                display:flex;
                flex-flow:row wrap;
                justify-content:flex-end;
                span {padding:0 15px 0 0;}
                > span, > div {
                    flex:1 100%;
                    max-width:133px;
                    text-align:left;
                }
            }
        `,
    },

    select: {
        labelProps: {
            css: css`
                width: unset;
                color: ${themeColors.colorCharcoalGrey};
                font-family: Roboto , sans-serif;
                font-size: ${themeFontSizes.size14};
                letter-spacing: .01rem;
                font-weight: normal;
                text-overflow: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: inherit;
                justify-content: right;

                > div {
                    width: auto;
                    min-width: 11.5rem;

                    @media screen and (min-width: 768px) and (max-width: 1200px) { 
                        min-width: initial;
                    }
                }

                select {
                    border: 1px solid ${themeColors.colorBorderBrands};
                    border-radius: .125rem;
                    background-color: ${themeColors.colorWhite};
                    color: ${themeColors.colorCharcoalGrey};
                    font-family: Roboto , sans-serif;
                    font-size: ${themeFontSizes.size14};
                    letter-spacing: .01rem;
                    line-height: 1.125rem;
                    &:focus {
                       outline: rgb(32, 96, 173) dashed 2px !important;  
                    }
                }
            `,
        },
    },
    textField: {
        cssOverrides: {
            formField: css`
                width: inherit;
                justify-content: right;
                input {
                    min-width: 11.5rem;
                    @media screen and (min-width: 768px) and (max-width: 1200px) { 
                        min-width: initial;
                    }
                }
            `,
        },
    },
    labelText: {
        css: css`
            font-size: ${themeFontSizes.size14};
            color: ${themeColors.colorCharcoalGrey};
            padding:0;
        `,
    },
};

const styles = productListControlStyles;

const ProductListControls: FC<Props> = ({ addProductFilters, pagination }) => {

    if (!pagination) {
        return null;
    }
    const [pageInput, setPageInput] = useState("");
    const [error, setError] = useState("");

    const totalPages = Math.ceil(pagination.totalItemCount / pagination.pageSize);
    const totalItemCount = pagination.totalItemCount;
    
    const sortOptions = pagination.sortOptions;

    const rental = sortOptions.find(o => o.displayName === "Rental");
    const used = sortOptions.find(o => o.displayName === "Used");
    const reorderedSortOptions = sortOptions.filter(
        o => o.displayName !== "Rental" && o.displayName !== "Used"
    );
    const bestMatchIndex = reorderedSortOptions.findIndex(
        o => o.displayName === "Best Match"
    );
    if (rental && used && bestMatchIndex !== -1) {
        reorderedSortOptions.splice(bestMatchIndex + 1, 0, rental, used);
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addProductFilters({ sort: event.currentTarget.value });
    };

    /* ------------------------------
       JUMP TO PAGE LOGIC
    ------------------------------ */

    const changePage = (page: number) => {
        addProductFilters({ page });
        window.scrollTo(0, 0);
    };

    const handleJump = (value?: string) => {
        const inputValue = value ?? pageInput;

        if (!inputValue) {
            setError("Please enter a valid page number.");
            return;
        }

        if (!/^[1-9][0-9]*$/.test(inputValue)) {
            setError("Please enter a valid positive page number.");
            return;
        }

        let pageNum = Number(inputValue);

        if (pageNum < 1 || pageNum > totalPages) {
            setError("Page number not available, redirecting to nearest match.");
            pageNum = Math.min(Math.max(pageNum, 1), totalPages);
            changePage(pageNum);
            setPageInput(pageNum.toString());
            return;
        }

        setError("");
        changePage(pageNum);
        setPageInput(pageNum.toString());
    };
    
    return (
        <>
        {!!totalItemCount && (
            <StyledWrapper {...styles.wrapper}>
                {/* New column based layout for easier organization on mobile */}
                <StyledWrapper {...styles.leftColumn}>
                    <StyledWrapper {...styles.resultsCount}>
                        <ProductCountDisplay
                            totalItemCount={pagination?.totalItemCount}
                            pageSize={pagination?.pageSize}
                        />
                    </StyledWrapper>                    
                    <StyledWrapper {...styles.resultsPerPage}>
                        {/* We hide the page navigation here for better layout control */}
                        <ProductListPagination
                            pagination={pagination}
                            addProductFilters={addProductFilters}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.centerColumn}>
                    <StyledWrapper {...styles.paginationNav}>
                        {/* We duplicate the pagination element and hide the per page drop down for better layout control */}
                        <ProductListPagination
                            pagination={pagination}
                            addProductFilters={addProductFilters}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    <StyledWrapper {...styles.sortBySelect}>
                        <Select
                            {...styles.select}
                            label={translate("Sort by")}
                            value={pagination.sortType}
                            labelPosition="left"
                            onChange={onChangeSortHandler}
                            data-test-selector="productListSortSelect"
                            >
                            {reorderedSortOptions.map(sortOption => (
                            <option key={sortOption.sortType} value={sortOption.sortType}>
                            {sortOption.displayName}
                            </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                    <StyledWrapper {...styles.jumpToPage}>
                        <Typography {...styles.labelText}>
                            {translate("Jump to page")}
                        </Typography>
                        <TextField
                            value={pageInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setPageInput(e.target.value);
                                setError("");
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                const value = (e.target as HTMLInputElement).value;

                                if (e.key === "Enter") handleJump(value);
                                if (e.key === "Tab") setTimeout(() => handleJump(value), 0);
                            }}
                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                handleJump(e.target.value)
                            }
                            {...styles.textField}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        )}

        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListControls),
    definition: {
        group: "Product List",
        displayName: "Product List Controls",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;
